import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { withSite } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BatchEditor } from "@/lib/batch/BatchEditor";
import { reconcile, markDuplicates } from "@/lib/batch/batchCore";
import { Plus, ClipboardCheck, Check, X, Trash2, Pencil, Copy, ArrowUp, ArrowDown, Layers } from "lucide-react";

const RESPONSE_TYPES = [{ v: "yes_no", l: "Yes / No" }, { v: "checkbox", l: "Checkbox" }, { v: "number", l: "Number" }, { v: "text", l: "Text" }, { v: "temperature", l: "Temperature" }];

export default function Checklists() {
  const { has } = useAuth();
  const [today, setToday] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modal, setModal] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [batchTpl, setBatchTpl] = useState(null);

  const duplicate = async (cid) => {
    try { await api.post(`/checklists/templates/${cid}/duplicate`, {}); toast.success("Checklist duplicated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  const load = async () => {
    const [t, l, tp] = await Promise.all([api.get(withSite("/checklists/today")), api.get(withSite("/checklists/ledger")), api.get("/checklists/templates")]);
    setToday(t.data); setLedger(l.data); setTemplates(tp.data);
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("cc-site-changed", h); return () => window.removeEventListener("cc-site-changed", h); }, []);

  const [pending, setPending] = useState({});
  const respond = async (iid, item_id, response) => {
    const key = `${iid}:${item_id}`;
    if (pending[key]) return;
    setPending((p) => ({ ...p, [key]: true }));
    try {
      const { data } = await api.post(`/checklists/instances/${iid}/respond`, { item_id, response });
      setToday((list) => list.map((c) => (c.instance_id === iid ? data : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not saved — please try again");
    } finally {
      setPending((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };

  return (
    <div>
      <PageHeader title="Daily checklist" subtitle="Opening & closing checks that repeat automatically." testid="checklists-header"
        action={has("manage_checklists") && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-checklist-btn" onClick={() => setModal(true)}><Plus size={18} className="mr-1" /> New checklist</Button>} />

      {has("manage_checklists") && <p className="mb-4"><Link className="underline text-brand" to="/food-safety-setup">Food safety setup — use standard samples, customise a template or start from scratch</Link></p>}
      <Tabs defaultValue="active">
        <TabsList className="mb-5"><TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger><TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger><TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger></TabsList>

        <TabsContent value="active" className="space-y-5">
          {today.length === 0 ? <EmptyState icon={ClipboardCheck} title="No checklists yet." message="Create an opening or closing checklist." /> :
            today.map((c) => (
              <div key={c.instance_id} className="cc-card overflow-hidden" data-testid={`checklist-${c.instance_id}`}>
                <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <div><span className="font-display font-bold text-slate-800 capitalize">{c.name}</span><span className="text-xs text-slate-400 ml-2">{c.date}</span></div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="divide-y divide-slate-100">
                  {c.responses.map((r) => (
                    <div key={r.item_id} className="px-5 py-3.5 flex items-center justify-between gap-3" data-testid={`checklist-item-${r.item_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700">{r.title}</div>
                        <div className="text-[11px] text-slate-400">Notification hour: {r.notification_time || "—"}{r.completed_by_name && ` · ${r.completed_by_name} · ${fmtDateTime(r.completed_at)}`}</div>
                      </div>
                      <ItemInput r={r} pending={!!pending[`${c.instance_id}:${r.item_id}`]} disabled={!has("complete_checklists")} onRespond={(val) => respond(c.instance_id, r.item_id, val)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="ledger" className="space-y-2">
          {ledger.length === 0 ? <EmptyState icon={ClipboardCheck} title="No checklist history yet." /> :
            ledger.map((c) => {
              const done = c.responses.filter((r) => r.response !== null).length;
              return (
                <div key={c.instance_id} className="cc-list-row" data-testid={`ledger-${c.instance_id}`}>
                  <div><div className="font-semibold text-slate-800 capitalize">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.date} · {done}/{c.responses.length} answered{c.template_revision ? ` · rev ${c.template_revision}` : ""}</div></div>
                  <StatusBadge status={c.status} />
                </div>
              );
            })}
        </TabsContent>

        <TabsContent value="templates" className="space-y-2">
          {templates.length === 0 ? <EmptyState icon={ClipboardCheck} title="No checklist templates yet." message="Create an opening or closing checklist." /> :
            templates.map((t) => (
              <div key={t.checklist_id} className="rounded-xl border border-slate-100 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`template-${t.checklist_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 capitalize flex items-center gap-2 flex-wrap">
                    <span className="truncate">{t.name}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0" data-testid={`template-rev-${t.checklist_id}`}>rev {t.revision || 1}</span>
                  </div>
                  <div className="text-xs text-slate-500 capitalize">{t.type} · {t.schedule?.frequency || "daily"} · {(t.items || []).filter((i) => i.active !== false).length} items</div>
                </div>
                {has("manage_checklists") && (
                  <div className="flex flex-wrap gap-2 sm:shrink-0">
                    {t.schedule?.frequency === "manual" && <Button size="sm" onClick={async () => { try { await api.post(`/checklists/templates/${t.checklist_id}/run`); toast.success("Today’s checklist is ready in Active"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not start checklist"); } }}>Start today</Button>}
                    <Button variant="outline" size="sm" className="rounded-lg whitespace-nowrap" data-testid={`batch-items-${t.checklist_id}`} onClick={() => setBatchTpl(t)}><Layers size={14} className="mr-1" /> Batch add items</Button>
                    <Button variant="outline" size="sm" className="rounded-lg whitespace-nowrap" data-testid={`edit-template-${t.checklist_id}`} onClick={() => setEditTpl(t)}><Pencil size={14} className="mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" className="rounded-lg whitespace-nowrap" data-testid={`duplicate-template-${t.checklist_id}`} onClick={() => duplicate(t.checklist_id)}><Copy size={14} className="mr-1" /> Duplicate</Button>
                    <Button variant="outline" size="sm" className="rounded-lg whitespace-nowrap text-red-600 hover:text-red-700" data-testid={`archive-template-${t.checklist_id}`} onClick={async () => { if (!window.confirm(`Archive "${t.name}"? Completed checklist runs stay unchanged.`)) return; try { await api.delete(`/checklists/templates/${t.checklist_id}`); toast.success("Checklist archived"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to archive"); } }}><Trash2 size={14} className="mr-1" /> Archive</Button>
                  </div>
                )}
              </div>
            ))}
        </TabsContent>
      </Tabs>

      {modal && <TemplateModal onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
      {editTpl && <EditTemplateModal tpl={editTpl} onClose={() => setEditTpl(null)} onSaved={() => { setEditTpl(null); load(); }} />}
      {batchTpl && <BatchAddChecklistItems tpl={batchTpl} onClose={() => setBatchTpl(null)} onDone={load} />}
    </div>
  );
}

function ItemInput({ r, onRespond, disabled, pending }) {
  const busy = pending ? "opacity-60 animate-pulse ring-2 ring-brand/40" : "";
  if (r.response_type === "yes_no") {
    return (
      <div className="flex gap-2 shrink-0" aria-busy={pending} data-testid={pending ? `resp-saving-${r.item_id}` : undefined}>
        <button disabled={disabled || pending} data-testid={`resp-yes-${r.item_id}`} onClick={() => onRespond("yes")} className={`h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1 ${busy} ${r.response === "yes" ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`}><Check size={15} /> Yes</button>
        <button disabled={disabled || pending} data-testid={`resp-no-${r.item_id}`} onClick={() => onRespond("no")} className={`h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1 ${busy} ${r.response === "no" ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600"}`}><X size={15} /> No</button>
      </div>
    );
  }
  if (r.response_type === "checkbox") {
    return <button disabled={disabled || pending} aria-busy={pending} data-testid={`resp-check-${r.item_id}`} onClick={() => onRespond(r.response ? null : "done")} className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors duration-100 ${busy} ${r.response ? "bg-brand text-white" : "bg-slate-100 text-slate-400"}`}><Check size={18} /></button>;
  }
  return <Input disabled={disabled} defaultValue={r.response || ""} type={r.response_type === "number" || r.response_type === "temperature" ? "number" : "text"} data-testid={`resp-input-${r.item_id}`} onBlur={(e) => e.target.value && onRespond(e.target.value)} className="w-28 h-9" placeholder={r.response_type === "temperature" ? "°C" : ""} />;
}

function TemplateModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("opening");
  const [items, setItems] = useState([{ title: "", response_type: "yes_no", notification_time: "08:00", required: true }]);
  const upd = (i, k, v) => { const n = [...items]; n[i][k] = v; setItems(n); };
  const save = async () => {
    const clean = items.filter((it) => it.title.trim());
    if (!name || !clean.length) return toast.error("Name and at least one item required");
    await api.post("/checklists/templates", { name, type, items: clean }); toast.success("Checklist created"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="checklist-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>New checklist</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="checklist-name" className="mt-1.5" placeholder="Opening checklist" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger data-testid="checklist-type" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="opening">Opening</SelectItem><SelectItem value="closing">Closing</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select></div>
          </div>
          <Label className="text-xs font-semibold text-slate-600">Items</Label>
          {items.map((it, i) => (
            <div key={i} className="border rounded-xl p-3 space-y-2" data-testid={`checklist-item-row-${i}`}>
              <div className="flex gap-2">
                <Input placeholder="Item title" value={it.title} onChange={(e) => upd(i, "title", e.target.value)} className="flex-1" />
                <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-red-400"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={it.response_type} onValueChange={(v) => upd(i, "response_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{RESPONSE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select>
                <Input type="time" value={it.notification_time} onChange={(e) => upd(i, "notification_time", e.target.value)} />
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="add-checklist-item" onClick={() => setItems([...items, { title: "", response_type: "yes_no", notification_time: "08:00", required: true }])}><Plus size={14} className="mr-1" /> Add item</Button>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-checklist" className="bg-brand hover:bg-brand-hover">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditTemplateModal({ tpl, onClose, onSaved }) {
  const [name, setName] = useState(tpl.name || "");
  const [type, setType] = useState(tpl.type || "opening");
  const [items, setItems] = useState((tpl.items || []).filter((i) => i.active !== false).map((i) => ({
    item_id: i.item_id, title: i.title || "", response_type: i.response_type || "yes_no",
    notification_time: i.notification_time || "", required: i.required !== false, description: i.description || "",
  })));
  const [schedule, setSchedule] = useState(tpl.schedule || null);
  const [saving, setSaving] = useState(false);
  const upd = (idx, k, v) => setItems(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const n = [...items];
    [n[idx], n[j]] = [n[j], n[idx]];
    setItems(n);
  };
  const save = async () => {
    const clean = items.filter((it) => it.title.trim());
    if (!name.trim() || !clean.length) return toast.error("Name and at least one item required");
    setSaving(true);
    try {
      await api.put(`/checklists/templates/${tpl.checklist_id}`, {
        name, type, items: clean, expected_revision: tpl.revision || 1, ...(schedule ? { schedule } : {}),
      });
      toast.success("Checklist updated — future runs will use the new revision");
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) toast.error(e.response.data?.detail || "This checklist was changed by someone else. Please reload.");
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-checklist-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>Edit checklist · rev {tpl.revision || 1}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-checklist-name" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger data-testid="edit-checklist-type" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="opening">Opening</SelectItem><SelectItem value="closing">Closing</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select></div>
          </div>
          {schedule && <div className="grid grid-cols-2 gap-2"><div><Label>Frequency</Label><select className="border rounded p-2 w-full" aria-label="Checklist frequency" value={schedule.frequency} onChange={e => setSchedule({ ...schedule, frequency: e.target.value })}>{["daily","weekly","four_weekly","manual"].map(v => <option key={v} value={v}>{v.replace("_"," ")}</option>)}</select></div><div><Label>First due date</Label><Input type="date" value={schedule.start_date} onChange={e => setSchedule({ ...schedule, start_date: e.target.value })} /></div></div>}
          <Label className="text-xs font-semibold text-slate-600">Items</Label>
          {items.map((it, i) => (
            <div key={it.item_id || i} className="border rounded-xl p-3 space-y-2" data-testid={`edit-item-row-${i}`}>
              <div className="flex gap-2 items-center">
                <Input placeholder="Item title" value={it.title} onChange={(e) => upd(i, "title", e.target.value)} className="flex-1" data-testid={`edit-item-title-${i}`} />
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 disabled:opacity-30" data-testid={`edit-item-up-${i}`}><ArrowUp size={16} /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-slate-400 disabled:opacity-30" data-testid={`edit-item-down-${i}`}><ArrowDown size={16} /></button>
                <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-red-400" data-testid={`edit-item-remove-${i}`}><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={it.response_type} onValueChange={(v) => upd(i, "response_type", v)}><SelectTrigger data-testid={`edit-item-type-${i}`}><SelectValue /></SelectTrigger><SelectContent>{RESPONSE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select>
                <Input type="time" value={it.notification_time} onChange={(e) => upd(i, "notification_time", e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={it.required} onChange={(e) => upd(i, "required", e.target.checked)} data-testid={`edit-item-required-${i}`} className="accent-brand" /> Required
              </label>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="edit-add-item" onClick={() => setItems([...items, { title: "", response_type: "yes_no", notification_time: "08:00", required: true }])}><Plus size={14} className="mr-1" /> Add item</Button>
          <p className="text-[11px] text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="edit-warning">
            Changes will apply to <strong>future</strong> checklist runs. Existing completed and in-progress records will not be changed.
          </p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-edit-checklist" className="bg-brand hover:bg-brand-hover">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const CK_RTYPE_ALIASES = {
  "yes/no": "yes_no", "yes / no": "yes_no", "yesno": "yes_no", "yes_no": "yes_no", "y/n": "yes_no",
  "checkbox": "checkbox", "check": "checkbox", "tick": "checkbox",
  "number": "number", "num": "number", "text": "text", "note": "text",
  "temperature": "temperature", "temp": "temperature",
};
const normRType = (v) => CK_RTYPE_ALIASES[String(v || "").trim().toLowerCase()] || String(v || "yes_no").trim().toLowerCase() || "yes_no";
const isRequiredValue = (v) => !/^(no|false|optional|n|0)$/i.test(String(v ?? "yes").trim());

function BatchAddChecklistItems({ tpl, onClose, onDone }) {
  const existingTitles = (tpl.items || []).filter((i) => i.active !== false).map((i) => i.title);
  const columns = [
    { key: "title", label: "Item text", placeholder: "e.g. Check fridge temperatures" },
    { key: "required", label: "Required", type: "select", default: "yes",
      options: [{ value: "yes", label: "Required" }, { value: "no", label: "Optional" }] },
    { key: "response_type", label: "Response", type: "select", default: "yes_no",
      options: RESPONSE_TYPES.map((t) => ({ value: t.v, label: t.l })) },
  ];
  const validateRow = (v) => {
    const errors = {};
    if (!String(v.title || "").trim()) errors.title = "Required";
    return { errors, warnings: [] };
  };
  const annotate = (rows) => markDuplicates(rows, { field: "title", existing: existingTitles });
  const aliasMap = {
    title: ["item", "item text", "title", "name", "task"],
    required: ["required", "mandatory"],
    response_type: ["response type", "response", "type"],
  };
  const onCreate = async (subset, allRows) => {
    const payloadRows = subset.map((r) => ({
      title: String(r.values.title || "").trim(),
      required: isRequiredValue(r.values.required ?? "yes"),
      response_type: normRType(r.values.response_type),
    }));
    const operation_id = (crypto.randomUUID && crypto.randomUUID()) || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post(`/checklists/${tpl.checklist_id}/items/batch`, {
        operation_id, expected_revision: tpl.revision || 1, rows: payloadRows,
      });
      const next = reconcile(allRows, subset, data.results || []);
      const parts = [`${data.added} added`];
      if (data.duplicates) parts.push(`${data.duplicates} duplicate`);
      if (data.invalid) parts.push(`${data.invalid} invalid`);
      const msg = parts.join(", ");
      if (data.revised) { toast.success(`${msg} — now rev ${data.new_revision}`); onDone && onDone(); }
      else toast.message(`No items added (${msg})`);
      return next;
    } catch (e) {
      if (e.response?.status === 409) toast.error(e.response.data?.detail || "This checklist changed — reload and review before adding.");
      else toast.error(e.response?.data?.detail || "Batch add failed");
      return null;
    }
  };
  return (
    <BatchEditor
      columns={columns} validateRow={validateRow} annotate={annotate} pasteColumn="title" aliasMap={aliasMap}
      destinationLabel={`${tpl.name} · Rev ${tpl.revision || 1}`}
      notice="This will create a new checklist revision for future runs. Existing completed and in-progress runs will not be changed."
      createLabel="Add items" onCreate={onCreate} onClose={onClose} />
  );
}

